import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BizyState } from '../../bizy';

const STORAGE_KEY = 'bizy-admin-whats-new-tip';

export interface WhatsNewTip {
	id: string;
	headlineKey: string;
	promptKey: string;
	state: BizyState;
	/** Internal note; tips with a comment are excluded from the daily rotation. */
	comment?: string;
}

/** Tips eligible for the admin empty-state daily rotation (no comment set). */
export function isTipInRotation(tip: WhatsNewTip): boolean {
	return !tip.comment?.trim();
}

export const WHATS_NEW_TIPS: WhatsNewTip[] = [
	{
		id: 'program-health',
		headlineKey: 'whatsNew.tips.programHealth.headline',
		promptKey: 'whatsNew.tips.programHealth.prompt',
		state: 'excited',
	},
	{
		id: 'struggling',
		headlineKey: 'whatsNew.tips.struggling.headline',
		promptKey: 'whatsNew.tips.struggling.prompt',
		state: 'excited',
		comment: 'Link in prompt is not working — excluded from rotation until fixed.',
	},
	{
		id: 'culture-champions',
		headlineKey: 'whatsNew.tips.cultureChampions.headline',
		promptKey: 'whatsNew.tips.cultureChampions.prompt',
		state: 'excited',
	},
	{
		id: 'combine-data',
		headlineKey: 'whatsNew.tips.combineData.headline',
		promptKey: 'whatsNew.tips.combineData.prompt',
		state: 'excited',
	},
	{
		id: 'export-data',
		headlineKey: 'whatsNew.tips.exportData.headline',
		promptKey: 'whatsNew.tips.exportData.prompt',
		state: 'excited',
		comment:
			'For admins the link is correct but Bizy answers with activity for the current user only, not company redemptions. Confirm expected behavior for admin vs non-admin before re-enabling.',
	},
	{
		id: 'recognition-gaps',
		headlineKey: 'whatsNew.tips.recognitionGaps.headline',
		promptKey: 'whatsNew.tips.recognitionGaps.prompt',
		state: 'excited',
		comment:
			'Bizy assumes manager-to-direct-report recognition and asks for more info instead of answering. Should answer company-wide and optionally ask a follow-up. Confirm behavior before re-enabling.',
	},
	{
		id: 'strongest-bonds',
		headlineKey: 'whatsNew.tips.strongestBonds.headline',
		promptKey: 'whatsNew.tips.strongestBonds.prompt',
		state: 'excited',
	},
	{
		id: 'super-connectors',
		headlineKey: 'whatsNew.tips.superConnectors.headline',
		promptKey: 'whatsNew.tips.superConnectors.prompt',
		state: 'excited',
	},
	{
		id: 'new-hires',
		headlineKey: 'whatsNew.tips.newHires.headline',
		promptKey: 'whatsNew.tips.newHires.prompt',
		state: 'excited',
		comment: 'Excluded from rotation, prompt needs to be revised.',
	},
];

interface StoredTipState {
	lastShownDate: string;
	lastTipIndex: number;
	dismissed: boolean;
	/** Tip IDs the user has already been shown; tips listed here are never re-assigned. */
	shownTipIds?: string[];
}

export function getDateKey(): string {
	return new Date().toISOString().split('T')[0];
}

function getStoredState(): StoredTipState | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return null;
		return JSON.parse(stored) as StoredTipState;
	} catch {
		return null;
	}
}

function setStoredState(state: StoredTipState): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		// localStorage unavailable
	}
}

/**
 * Returns the IDs of tips the user has already been shown.
 *
 * For state stored before `shownTipIds` existed, every rotating tip at indices
 * `[0..lastTipIndex]` is treated as already shown — that's the most we can infer
 * from the legacy single-index cursor.
 */
export function getShownTipIds(stored: StoredTipState | null): string[] {
	if (!stored) return [];
	if (stored.shownTipIds) return stored.shownTipIds;
	const through = stored.lastTipIndex;
	if (through === undefined || through < 0) return [];
	return WHATS_NEW_TIPS.slice(0, through + 1)
		.filter(isTipInRotation)
		.map((tip) => tip.id);
}

/**
 * Returns the index of the next rotating tip the user hasn't been shown yet,
 * scanning `WHATS_NEW_TIPS` in order. Returns `null` once every rotating tip
 * has been shown, so callers can stop displaying tips entirely.
 */
export function findNextUnshownTipIndex(shownTipIds: readonly string[]): number | null {
	for (let i = 0; i < WHATS_NEW_TIPS.length; i++) {
		const tip = WHATS_NEW_TIPS[i];
		if (tip && isTipInRotation(tip) && !shownTipIds.includes(tip.id)) {
			return i;
		}
	}
	return null;
}

interface UseAdminWhatsNewTipOptions {
	isAdmin: boolean;
	isEmptyState: boolean;
	isPanelOpen: boolean;
}

interface UseAdminWhatsNewTipResult {
	tip: WhatsNewTip | null;
	dismiss: () => void;
	shouldShowTip: boolean;
	/**
	 * True when there is a tip assigned for today that the user has not yet
	 * dismissed, regardless of whether it's currently visible (panel closed,
	 * chat non-empty, etc.). Useful for engagement-based dismissal triggers
	 * that need to fire when the tip would otherwise re-appear later.
	 */
	hasActiveTip: boolean;
}

/**
 * Picks which "What's new" tip to show admins in the empty Bizy panel.
 *
 * One tip per calendar day (UTC). Each tip is shown to the admin at most once
 * (per browser, via `localStorage`); after every rotating tip has been shown,
 * the hook returns `null` and admins see the regular empty state.
 */
function arraysShallowEqual(a: readonly string[], b: readonly string[]): boolean {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

export function useAdminWhatsNewTip({ isAdmin, isEmptyState, isPanelOpen }: UseAdminWhatsNewTipOptions): UseAdminWhatsNewTipResult {
	// `localStorage` is the source of truth across tabs. React state mirrors
	// storage so `useMemo` can react to changes; a sync effect below keeps the
	// state in lockstep with storage on focus / visibility / `storage` events.
	const [shownTipIds, setShownTipIds] = useState<string[]>(() => getShownTipIds(getStoredState()));
	// `dateKey` only acts as an invalidation handle for the memo below. Date
	// comparisons against `localStorage` always read `getDateKey()` fresh so a
	// stale React value (e.g. between sync ticks past UTC midnight) cannot
	// reuse yesterday's tip.
	const [dateKey, setDateKey] = useState(() => getDateKey());
	const [isDismissedToday, setIsDismissedToday] = useState(() => {
		const stored = getStoredState();
		return Boolean(stored && stored.lastShownDate === getDateKey() && stored.dismissed);
	});

	useEffect(() => {
		const sync = () => {
			const stored = getStoredState();
			const storedIds = getShownTipIds(stored);
			setShownTipIds((prev) => (arraysShallowEqual(prev, storedIds) ? prev : storedIds));
			const current = getDateKey();
			setDateKey((prev) => (prev === current ? prev : current));
			setIsDismissedToday(Boolean(stored && stored.lastShownDate === current && stored.dismissed));
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') sync();
		};
		const onStorage = (event: StorageEvent) => {
			if (event.key === STORAGE_KEY) sync();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('focus', sync);
		// `storage` fires in OTHER tabs when localStorage changes, so this keeps
		// multi-tab sessions in sync without clobbering each other's progress.
		window.addEventListener('storage', onStorage);

		// Schedule a precise sync just past the next UTC midnight so a tab kept
		// open across midnight picks up the new day in seconds rather than
		// waiting on the next focus/visibility event. After firing, schedule
		// the next midnight in turn.
		let midnightTimeoutId = 0;
		const scheduleMidnight = () => {
			const now = new Date();
			const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 5);
			midnightTimeoutId = window.setTimeout(() => {
				sync();
				scheduleMidnight();
			}, next - now.getTime());
		};
		scheduleMidnight();

		return () => {
			document.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('focus', sync);
			window.removeEventListener('storage', onStorage);
			window.clearTimeout(midnightTimeoutId);
		};
	}, []);

	// Resolve today's tip from localStorage, or pick the next unshown tip when the date rolls over.
	const { tip, tipIndex, needsPersist, currentDate } = useMemo(() => {
		// Always compute the current UTC date fresh inside the memo. The
		// `dateKey` dep just invalidates the cached result; we never read it
		// for comparisons, in case it's briefly stale between sync ticks.
		const today = getDateKey();
		if (!isAdmin) return { tip: null, tipIndex: -1, needsPersist: false, currentDate: today };

		const stored = getStoredState();

		if (stored && stored.lastShownDate === today) {
			// Today's tip is already locked in; reuse it unless dismissed.
			if (stored.dismissed) {
				return { tip: null, tipIndex: stored.lastTipIndex, needsPersist: false, currentDate: today };
			}
			const storedTip = WHATS_NEW_TIPS[stored.lastTipIndex];
			if (storedTip && isTipInRotation(storedTip)) {
				return { tip: storedTip, tipIndex: stored.lastTipIndex, needsPersist: false, currentDate: today };
			}
			// Stored tip was removed from rotation (e.g. `comment` added) — hide for today.
			return { tip: null, tipIndex: stored.lastTipIndex, needsPersist: false, currentDate: today };
		}

		// New day or first visit: pick the next tip the user hasn't seen yet.
		const nextIndex = findNextUnshownTipIndex(shownTipIds);
		if (nextIndex === null) {
			// Every rotating tip has been shown; stop showing tips.
			return { tip: null, tipIndex: -1, needsPersist: false, currentDate: today };
		}
		const nextTip = WHATS_NEW_TIPS[nextIndex];
		// needsPersist: write today's date + index so revisits the same day reuse this tip.
		return { tip: nextTip, tipIndex: nextIndex, needsPersist: true, currentDate: today };
	}, [isAdmin, shownTipIds, dateKey]);

	useEffect(() => {
		if (!needsPersist) return;
		// Re-read storage at write time so a concurrent update from another tab
		// (e.g. additional `shownTipIds`, a dismissal) isn't clobbered.
		const stored = getStoredState();
		setStoredState({
			lastShownDate: currentDate,
			lastTipIndex: tipIndex,
			dismissed: false,
			shownTipIds: getShownTipIds(stored),
		});
	}, [needsPersist, tipIndex, currentDate]);

	const shouldShowTip = isAdmin && isEmptyState && isPanelOpen && tip !== null && !isDismissedToday;

	// Mark a tip as "shown" the first time the admin actually sees it. This is what
	// guarantees we never show the same tip twice; the daily assignment alone isn't
	// enough because the admin might never open the panel that day.
	useEffect(() => {
		if (!shouldShowTip || !tip) return;
		// Always merge with the current storage value so a sibling tab's
		// updates aren't overwritten by our stale React state.
		const stored = getStoredState();
		const storedIds = getShownTipIds(stored);
		if (storedIds.includes(tip.id)) {
			if (!arraysShallowEqual(shownTipIds, storedIds)) {
				setShownTipIds(storedIds);
			}
			return;
		}
		const updated = [...storedIds, tip.id];
		setShownTipIds(updated);
		setStoredState({
			lastShownDate: getDateKey(),
			lastTipIndex: tipIndex,
			dismissed: stored?.dismissed ?? false,
			shownTipIds: updated,
		});
	}, [shouldShowTip, tip, tipIndex, shownTipIds]);

	// Hide the tip for the rest of today. Tomorrow advances to the next unshown tip.
	// Also triggered when the user clicks the prompt or starts typing in the chat input.
	const dismiss = useCallback(() => {
		const stored = getStoredState();
		setStoredState({
			lastShownDate: getDateKey(),
			lastTipIndex: stored?.lastTipIndex ?? tipIndex,
			dismissed: true,
			shownTipIds: getShownTipIds(stored),
		});
		setIsDismissedToday(true);
	}, [tipIndex]);

	const hasActiveTip = isAdmin && tip !== null && !isDismissedToday;

	return {
		// `tip` is today's assigned tip whether or not it's currently rendered.
		// Consumers should gate rendering on `shouldShowTip`; dismissal-style
		// triggers can read `tip.id` for analytics regardless of visibility.
		tip,
		dismiss,
		shouldShowTip,
		hasActiveTip,
	};
}
